import { Collapse, Grid, Link } from '@mui/material'
import { KAddressField } from 'components/core/KAddressField'
import { KTextField } from 'components/core/KTextField'
import { useCurrentStore, useOrdering, useCurrentUser } from 'hooks'
import { useCallback, useState, useEffect, useMemo, useRef } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { SubmitOrderSectionProps } from './SubmitOrderSectionProps'
import { OrderDeliveryInfo } from './OrderDeliveryInfo'
import { OrderDeliveryByOwnerInfo } from './OrderDeliveryByOwnerInfo'
import { isEmpty } from 'lodash'
import { Address, AddressType } from 'models/Address'
import { useTheme } from '@mui/material/styles'
import { AddressSelectionDialog } from 'components/account/AddressSelectionDialog'
import { Place } from 'models'

export const OrderInfoDeliveryAddressInput = ({
  form,
}: SubmitOrderSectionProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [openAddressSelection, setOpenAddressSelection] = useState(false)
  const { currentUser } = useCurrentUser()
  const hasAutoSelectedAddress = useRef(false)

  const deliveryAddresses = useMemo(
    () => currentUser?.deliveryAddresses || [],
    [currentUser?.deliveryAddresses],
  )
  const hasSavedDeliveryAddresses = deliveryAddresses.length > 0
  const defaultAddress = useMemo(
    () => deliveryAddresses.find((address) => address.default),
    [deliveryAddresses],
  )

  const billingAddresses = useMemo(
    () => currentUser?.billingAddresses || [],
    [currentUser?.billingAddresses],
  )
  const defaultBillingAddress = useMemo(
    () => billingAddresses.find((address) => address.default),
    [billingAddresses],
  )

  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = form
  const { order, saveOrderProps } = useOrdering()
  const { store } = useCurrentStore()

  const orderType = order?.order_type
  const isDeliveryByOwner = store?.delivery_settings?.delivery_type === 'owner'
  const isOrderDelivery = orderType === 'delivery'

  const currentlySelectedAddress = useMemo(
    () =>
      deliveryAddresses.find(
        (address) => address.id === order?.customer_place?.saved_address_id,
      ),
    [deliveryAddresses, order?.customer_place?.saved_address_id],
  )

  const handleDeliveryFeeError = useCallback(
    (message?: string) => {
      if (isEmpty(message)) {
        clearErrors('orderInfo.deliveryFee')
      } else {
        setError('orderInfo.deliveryFee', { message })
      }
    },
    [setError, clearErrors],
  )

  const openSelectAddressDialog = () => {
    setOpenAddressSelection(true)
  }

  const handleSelectAddress = useCallback(
    (address: Address) => {
      if (!address) return

      const place: Place = {
        place_id: address.place_id,
        saved_address_id: address.id,
        description: address.address,
        street: address.street ?? address.address,
        city: address.city,
        state: address.state,
        zip_code: address.zip,
      }

      setValue('orderInfo.deliveryPlace', place, { shouldValidate: true })

      saveOrderProps({
        customer_place: place,
      })
    },
    [setValue, saveOrderProps],
  )

  const handleAutofill = useCallback(() => {
    const place: Place = {
      place_id: defaultAddress?.place_id,
      saved_address_id: defaultAddress?.id,
      description: defaultAddress?.address,
      street: defaultAddress?.street ?? defaultAddress?.address,
      city: defaultAddress?.city,
      state: defaultAddress?.state,
      zip_code: defaultAddress?.zip,
    }

    const billingPlace = {
      saved_address_id: defaultBillingAddress?.id,
      description: defaultBillingAddress?.address,
      street: defaultBillingAddress?.street ?? defaultBillingAddress?.address,
      city: defaultBillingAddress?.city,
      state: defaultBillingAddress?.state,
      zip_code: defaultBillingAddress?.zip,
    }

    if (place) {
      setValue('orderInfo.deliveryPlace', place, { shouldValidate: true })
    }

    setTimeout(() => {
      saveOrderProps({
        customer_place: isOrderDelivery ? place : undefined,
        customer_phone: currentUser?.phoneNumber,
        customer_first_name: currentUser?.firstName,
        customer_last_name: currentUser?.lastName,
        customer_email: currentUser?.email,
        billing_place: billingPlace,
        billing_address: billingPlace?.description,
        billing_city: billingPlace?.city,
        billing_state: billingPlace?.state,
        billing_zip: billingPlace?.zip_code,
        online_customer_id: currentUser?.id,
      })
    }, 0)
  }, [
    setValue,
    saveOrderProps,
    currentUser,
    defaultBillingAddress,
    isOrderDelivery,
    defaultAddress,
  ])

  const handleCloseAddressDialog = () => {
    setOpenAddressSelection(false)
  }

  useEffect(() => {
    if (
      currentUser &&
      !order?.online_customer_id &&
      !hasAutoSelectedAddress.current
    ) {
      handleAutofill()
      hasAutoSelectedAddress.current = true
    }
  }, [defaultAddress, handleAutofill, currentUser, order?.online_customer_id])

  return (
    <Collapse in={orderType === 'delivery'}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="orderInfo.deliveryPlace"
            control={control}
            rules={{
              validate: (value) => {
                if (orderType === 'delivery' && !value) {
                  return t('Delivery address is required')
                }
                return true
              },
            }}
            render={({ field }) => (
              <KAddressField
                required
                label={t('Delivery address')}
                helperText={errors?.orderInfo?.deliveryPlace?.message}
                error={!!errors?.orderInfo?.deliveryPlace}
                value={order?.customer_place}
                onChange={(value) => {
                  if (value?.place_id !== order?.customer_place?.place_id) {
                    field.onChange({ target: { value, name: field.name } })
                    saveOrderProps({ customer_place: value })
                  }
                }}
                ref={field.ref}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <KTextField
            label={t('Unit')}
            value={order?.customer_place_unit}
            onChange={(e) =>
              saveOrderProps({ customer_place_unit: e.target.value })
            }
          />
        </Grid>
        {currentUser && hasSavedDeliveryAddresses && (
          <Grid item xs={12} sm={3} display="flex" alignItems="center">
            <Link
              underline="hover"
              fontSize={theme.typography.body2.fontSize}
              sx={{
                cursor: 'pointer',
                color: theme.palette.primary.main,
                marginTop: 4,
              }}
              onClick={openSelectAddressDialog}
            >
              {t('Select from previous addresses')}
            </Link>
          </Grid>
        )}
      </Grid>
      {isDeliveryByOwner ? (
        <OrderDeliveryByOwnerInfo onError={handleDeliveryFeeError} />
      ) : (
        <OrderDeliveryInfo onError={handleDeliveryFeeError} />
      )}

      {currentUser && (
        <AddressSelectionDialog
          open={openAddressSelection}
          onClose={handleCloseAddressDialog}
          addresses={deliveryAddresses}
          addressType={AddressType.Delivery}
          onSelectAddress={handleSelectAddress}
          selectedAddress={currentlySelectedAddress}
        />
      )}
    </Collapse>
  )
}
